(() => {
  const gcd = (a, b) => b ? gcd(b, a % b) : a;
  const stepFor = (pack = 1, increment = 1) => {
    const a = Math.max(1, Number(pack) || 1), b = Math.max(1, Number(increment) || 1);
    return a * b / gcd(a, b);
  };
  const normalize = (value, pack = 1, min = 1, max = null, increment = 1) => {
    const step = stepFor(pack, increment);
    const floor = Math.ceil(Math.max(1, min, pack) / step) * step;
    const ceiling = max === null || max === '' ? Infinity : Math.floor(Number(max) / step) * step;
    if (ceiling < floor) return null;
    return Math.min(ceiling, Math.max(floor, Math.ceil((Number(value) || floor) / step) * step));
  };
  const groupsFor = context => context.count ? [...(context.threading ? [] : ['wax']), 'fluid', ...(context.mag ? ['hss', 'carbide'] : [])] : [];
  const fraction = value => String(value).split('-').reduce((sum, part) => {
    const [a, b] = part.split('/').map(Number);
    return sum + a / (b || 1);
  }, 0);
  const planCartUpdates = data => {
    if (data?.version !== 1 || !Array.isArray(data.items)) throw new Error('invalid');
    const updates = {}, keys = new Set();
    for (const item of data.items) {
      if (typeof item.key !== 'string' || !item.key || keys.has(item.key)) throw new Error('invalid');
      keys.add(item.key);
      for (const field of ['quantity', 'pack', 'min', 'increment']) {
        if (!Number.isSafeInteger(item[field]) || item[field] < 1) throw new Error('invalid');
      }
      if (item.max !== null && (!Number.isSafeInteger(item.max) || item.max < 0)) throw new Error('invalid');
      const quantity = normalize(item.quantity, item.pack, item.min, item.max, item.increment);
      if (quantity === null || !Number.isSafeInteger(quantity)) throw new Error('invalid');
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    return updates;
  };
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { normalize, groupsFor, fraction, stepFor, planCartUpdates };
    return;
  }
  const surfaces = [];
  let cartBusy = false;
  const initialize = drawer => {
  const isPage = drawer.hasAttribute('data-cto-order-page');
  const root = (window.routes?.rootUrl || '/').replace(/\/$/, '');
  const choices = new Map(), productCache = new Map();
  let context, catalogPromise, busy = false, previousFocus, renderedContext, wasOpen = false;
  const parseContext = () => {
    try { return JSON.parse(drawer.querySelector('[data-cto-order-context]').textContent); }
    catch { return { count: 0, strings: {}, currency: 'CAD', locale: 'en' }; }
  };
  const node = (tag, className, text) => {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  };
  const money = cents => new Intl.NumberFormat(context.locale, { style: 'currency', currency: context.currency, currencyDisplay: 'narrowSymbol' }).format(cents / 100);
  const announce = text => { const el = drawer.querySelector('[data-cto-order-live]'); if (el) el.textContent = text; };
  const updateHeader = () => {
    const count = Number(context.count) || 0;
    const unit = context.locale.startsWith('fr') ? (count === 1 ? 'article' : 'articles') : context.locale.startsWith('es') ? (count === 1 ? 'artículo' : 'artículos') : (count === 1 ? 'item' : 'items');
    document.querySelectorAll('.header__cart-count').forEach(el => { el.textContent = count; });
    document.querySelectorAll('.header__cart-toggle').forEach(el => {
      const title = el.querySelector('.header__cart-label strong')?.textContent.trim() || 'Cart';
      el.setAttribute('aria-label', `${title}, ${count} ${unit}`);
      const label = el.querySelector('.header__cart-label small');
      if (label) label.textContent = `${count} ${unit}`;
      el.querySelector('.header__cart-icon')?.classList.toggle('has-items', count > 0);
    });
  };
  const showError = error => {
    const el = drawer.querySelector('[data-cto-order-error]');
    if (el) { el.textContent = error?.message || context.strings.error; el.hidden = false; el.scrollIntoView({ block: 'nearest' }); }
    announce(error?.message || context.strings.error);
  };
  const setBusy = state => {
    busy = state;
    drawer.setAttribute('aria-busy', String(state));
    drawer.querySelectorAll('.quantity-selector button, .quantity-selector input, [data-cto-cart-checkout-button], [data-cto-extra-add]').forEach(el => {
      if (state) { el.dataset.wasDisabled = String(el.disabled); el.disabled = true; }
      else if (el.matches('[data-cto-extra-add]')) { el.disabled = el.dataset.ready !== 'true'; delete el.dataset.wasDisabled; }
      else if ('wasDisabled' in el.dataset) { el.disabled = el.dataset.wasDisabled === 'true'; delete el.dataset.wasDisabled; }
    });
    if (state) announce(context.strings.updating);
  };
  const request = async (url, options = {}) => {
    const response = await fetch(url, { credentials: 'same-origin', ...options });
    let data;
    try { data = await response.json(); } catch { throw new Error(context.strings.error); }
    if (!response.ok) throw new Error(typeof data.description === 'string' ? data.description : context.strings.error);
    return data;
  };
  const restoreFocus = bookmark => {
    if (!bookmark || drawer.getAttribute('aria-hidden') === 'true') return;
    let el;
    if (bookmark.key) {
      const row = Array.from(drawer.querySelectorAll('[data-cto-mini-cart-line-item]')).find(r => r.dataset.lineKey === bookmark.key);
      el = bookmark.action ? row?.querySelector(`[data-action="${bookmark.action}"]`) : row?.querySelector('input');
    } else if (bookmark.group) el = drawer.querySelector(`[data-extra-group="${bookmark.group}"] [data-cto-extra-add]`);
    (el || drawer.querySelector('[data-cto-mini-cart-close], h1'))?.focus({ preventScroll: true });
  };
  const refresh = async bookmark => {
    const content = drawer.querySelector('[data-cto-mini-cart-content]');
    const scrollTop = content?.scrollTop || 0;
    const extrasOpen = drawer.querySelector('[data-cto-order-extras]')?.open;
    const sectionId = isPage ? drawer.dataset.sectionId : 'mini-cart';
    const response = await fetch(`${root}/cart?section_id=${encodeURIComponent(sectionId)}`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(context.strings.error);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    const incoming = html.querySelector(isPage ? '[data-cto-order-page]' : '#mini-cart');
    if (!incoming) throw new Error(context.strings.error);
    if (isPage) {
      if (!Number(incoming.dataset.itemCount) || !Number(drawer.dataset.itemCount)) {
        window.location.reload();
        return;
      }
      drawer.querySelectorAll('[data-cto-page-fragment]').forEach(fragment => {
        const next = incoming.querySelector(`[data-cto-page-fragment="${fragment.dataset.ctoPageFragment}"]`);
        if (next) fragment.innerHTML = next.innerHTML;
      });
    } else drawer.innerHTML = incoming.innerHTML;
    for (const attr of ['data-item-count', 'data-cto-cart-item-count']) drawer.setAttribute(attr, incoming.getAttribute(attr) || '0');
    context = parseContext();
    updateHeader();
    const freshContent = drawer.querySelector('[data-cto-mini-cart-content]');
    if (freshContent) freshContent.scrollTop = scrollTop;
    const extras = drawer.querySelector('[data-cto-order-extras]');
    if (extras && extrasOpen !== undefined) extras.open = extrasOpen;
    if (isPage || drawer.getAttribute('aria-hidden') === 'false') await renderExtras();
    restoreFocus(bookmark);
    document.dispatchEvent(new CustomEvent(isPage ? 'cto:cart-page-updated' : 'cart:rerendered', { bubbles: true }));
    announce(context.strings.saved);
  };
  const mutate = async (path, payload, bookmark) => {
    if (cartBusy) return;
    cartBusy = true;
    surfaces.forEach(surface => surface.setBusy(true));
    try {
      await request(`${root}/cart/${path}.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify(payload) });
      await Promise.all(surfaces.map(surface => surface.refresh(surface.element === drawer ? bookmark : null)));
    } catch (error) {
      try { await refresh(bookmark); } catch {}
      showError(error);
    } finally { cartBusy = false; surfaces.forEach(surface => surface.setBusy(false)); }
  };
  const getProduct = row => {
    const key = `${context.currency}:${context.locale}:${row.handle}`;
    if (!productCache.has(key)) {
      productCache.set(key, request(`${root}/products/${encodeURIComponent(row.handle)}?view=cto-order-option`).catch(error => { productCache.delete(key); throw error; }));
    }
    return productCache.get(key);
  };
  const makeSelect = (card, key, label, options, selected) => {
    const wrap = node('label', 'cto-extra-field');
    wrap.append(node('span', '', label));
    const select = node('select');
    select.name = `cto-extra-${card.dataset.extraGroup}-${key}`;
    select.dataset.extraOption = key;
    for (const item of options) { const o = node('option', '', item.label || item); o.value = item.value || item; select.append(o); }
    select.value = selected;
    wrap.append(select);
    return wrap;
  };
  const buildCard = async (group, catalog) => {
    const rows = catalog.filter(row => row.group === group);
    if (!rows.length) return null;
    const card = node('article', 'cto-extra-card');
    card.dataset.extraGroup = group;
    const image = node('img', 'cto-extra-image'); image.width = 80; image.height = 80; image.alt = ''; image.loading = 'lazy'; image.hidden = true;
    const heading = node('div', 'cto-extra-heading');
    heading.append(node('span', 'cto-extra-brand', 'CHAMPION / BRUTELUBE'), node('h3', '', context.strings[group]), node('p', '', context.strings[`${group}_desc`]));
    if (group === 'hss' || group === 'carbide') heading.firstChild.textContent = 'CHAMPION / ROTOBRUTE';
    const top = node('div', 'cto-extra-top'); top.append(image, heading); card.append(top);
    const fields = node('div', 'cto-extra-fields');
    const saved = choices.get(group) || {};
    const cutter = group === 'hss' || group === 'carbide';
    if (cutter) {
      const depths = [...new Set(rows.map(r => r.depth))];
      const depth = depths.includes(saved.depth) ? saved.depth : depths[0];
      const diameters = [...new Set(rows.filter(r => r.depth === depth).map(r => r.diameter))].sort((a,b) => fraction(a)-fraction(b));
      const diameter = diameters.includes(saved.diameter) ? saved.diameter : diameters.includes('13/16') ? '13/16' : diameters[0];
      fields.append(makeSelect(card, 'diameter', context.strings.diameter, diameters.map(v=>({value:v,label:`${v}\u2033`})), diameter), makeSelect(card, 'depth', context.strings.depth, depths, depth));
    } else fields.append(makeSelect(card, 'sku', context.strings.format, rows.map(r=>({value:r.sku,label:r.label})), rows.some(r=>r.sku===saved.sku)?saved.sku:rows[0].sku));
    card.append(fields);
    const bottom = node('div', 'cto-extra-bottom');
    const prices = node('div', 'cto-extra-prices');
    const price = node('strong', 'cto-extra-price', context.strings.loading);
    const sku = node('span', 'cto-extra-sku');
    prices.append(price, sku);
    const add = node('button', 'cto-extra-add', context.strings.add); add.type = 'button'; add.dataset.ctoExtraAdd = ''; add.disabled = true;
    bottom.append(prices, add); card.append(bottom);
    const status = node('p', 'cto-extra-status'); status.setAttribute('aria-live', 'polite'); card.append(status);
    let selected, selectedQuantity = 1, revision = 0;
    const update = async changed => {
      const ownRevision = ++revision;
      let state = Object.fromEntries(Array.from(fields.querySelectorAll('select')).map(s=>[s.dataset.extraOption,s.value]));
      if (cutter && changed === 'depth') {
        const diameter = fields.querySelector('[data-extra-option="diameter"]');
        const sizes = [...new Set(rows.filter(r=>r.depth===state.depth).map(r=>r.diameter))].sort((a,b)=>fraction(a)-fraction(b));
        diameter.replaceChildren(...sizes.map(v=>{const o=node('option','',`${v}\u2033`);o.value=v;return o;}));
        diameter.value = sizes.includes(state.diameter) ? state.diameter : sizes.includes('13/16') ? '13/16' : sizes[0];
        state.diameter = diameter.value;
      }
      choices.set(group, state);
      selected = rows.find(r=>cutter ? r.diameter===state.diameter && r.depth===state.depth : r.sku===state.sku);
      add.disabled = true; add.dataset.ready = 'false'; price.textContent = context.strings.loading; status.textContent = '';
      sku.textContent = selected?.sku.replace(/^CHA-/, '') || '';
      if (!selected) { price.textContent = context.strings.unavailable; return; }
      try {
        const product = await getProduct(selected);
        if (ownRevision !== revision) return;
        const variant = product.variants.find(v=>v.id===selected.id && v.sku===selected.sku);
        selectedQuantity = normalize(product.pack, product.pack, variant?.min, variant?.max, variant?.increment);
        if (!variant?.available || selectedQuantity === null) throw new Error(context.strings.unavailable);
        price.textContent = `${money(variant.price * selectedQuantity)} ${context.currency}`;
        add.textContent = context.strings.add;
        add.dataset.ready = 'true'; add.disabled = busy;
        if (product.image) { image.src = product.image; image.hidden = false; }
        add.setAttribute('aria-label', `${context.strings.add}: ${selected.sku}, ${selectedQuantity}`);
      } catch { if (ownRevision === revision) { price.textContent = context.strings.unavailable; add.disabled = true; } }
    };
    fields.addEventListener('change', event => update(event.target.dataset.extraOption));
    add.addEventListener('click', async () => {
      if (!selected || add.disabled) return;
      add.textContent = context.strings.adding;
      await mutate('add', { items: [{ id: selected.id, quantity: selectedQuantity }] }, { group });
    });
    update();
    return card;
  };
  async function renderExtras() {
    context = parseContext();
    updateHeader();
    const grid = drawer.querySelector('[data-cto-order-extra-grid]');
    if (!grid || grid.dataset.rendering) return;
    grid.dataset.rendering = 'true';
    try {
      catalogPromise ||= request(context.catalog).catch(error=>{catalogPromise=null;throw error;});
      const catalog = await catalogPromise;
      const cards = await Promise.all(groupsFor(context).map(group=>buildCard(group,catalog)));
      if (!grid.isConnected) return;
      grid.replaceChildren(...cards.filter(Boolean));
      const note = drawer.querySelector('[data-cto-order-fit-note]');
      if (note) note.hidden = !context.mag;
    } catch {
      if (grid.isConnected) grid.textContent = context.strings.error;
      delete grid.dataset.rendering;
    }
  }
  surfaces.push({ element: drawer, refresh, setBusy, showError });
  const close = () => {
    const trigger = document.querySelector('.header__action-item--cart [data-action="toggle-mini-cart"]');
    if (drawer.getAttribute('aria-hidden') === 'false') trigger?.click();
  };
  if (!isPage) {
  const overlay = node('div', 'cto-order-overlay'); overlay.setAttribute('aria-hidden','true'); document.body.append(overlay);
  overlay.addEventListener('click', close);
  drawer.addEventListener('transitionend', event => {
    if (event.target === drawer && event.propertyName === 'transform' && drawer.getAttribute('aria-hidden') === 'false' && !drawer.contains(document.activeElement)) {
      drawer.querySelector('[data-cto-mini-cart-close]')?.focus({ preventScroll: true });
    }
  });
  const syncOpen = () => {
    const open = drawer.getAttribute('aria-hidden') === 'false';
    document.documentElement.classList.toggle('cto-order-open', open);
    drawer.inert = !open;
    if (open && !wasOpen) {
      previousFocus = document.activeElement;
      renderExtras();
      requestAnimationFrame(()=>drawer.querySelector('[data-cto-mini-cart-close]')?.focus({preventScroll:true}));
    }
    if (!open && wasOpen) {
      (previousFocus?.isConnected && !drawer.contains(previousFocus) ? previousFocus : document.querySelector('.header__action-item--cart [data-action="toggle-mini-cart"]'))?.focus({preventScroll:true});
    }
    wasOpen = open;
  };
  new MutationObserver(records => {
    if (records.some(r=>r.type==='attributes')) syncOpen();
    if (records.some(r=>r.type==='childList')) {
      context = parseContext();
      if (drawer.getAttribute('aria-hidden')==='false') renderExtras();
    }
  }).observe(drawer, {childList:true, attributes:true, attributeFilter:['aria-hidden']});
  syncOpen();
  }
  drawer.addEventListener('click', event => {
    if (event.target.closest('[data-cto-mini-cart-close]')) { event.preventDefault(); event.stopImmediatePropagation(); close(); return; }
    const button = event.target.closest('[data-action="increase-quantity"], [data-action="decrease-quantity"]');
    if (!button) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy || button.disabled) return;
    const row = button.closest('[data-line-key]'), input = row?.querySelector('input.quantity-selector__value');
    if (!row || !input) return;
    const value = button.classList.contains('cto-mini-cart__remove') || button.classList.contains('cto-cart__remove') ? 0 : normalize(Number(input.value) + (button.dataset.action==='increase-quantity'?1:-1)*stepFor(input.step,input.dataset.ruleIncrement),input.step,input.min,input.max,input.dataset.ruleIncrement);
    if (value === null) return;
    mutate('change', {id:row.dataset.lineKey, quantity:value}, {key:row.dataset.lineKey,action:button.dataset.action});
  }, true);
  drawer.addEventListener('change', event => {
    const input = event.target.closest('.quantity-selector__value');
    if (!input) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (busy) return;
    const value = normalize(input.value,input.step,input.min,input.max,input.dataset.ruleIncrement);
    if (value===null) return;
    input.value = value;
    if (value===Number(input.dataset.currentValue)) return;
    mutate('change',{id:input.dataset.lineKey,quantity:value},{key:input.dataset.lineKey});
  }, true);
  drawer.addEventListener('submit', event => { if (cartBusy) event.preventDefault(); });
  drawer.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('.quantity-selector__value')) {
      event.preventDefault();
      event.target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
  if (isPage) {
    drawer.addEventListener('click', async event => {
      const button = event.target.closest('[data-action="save-note"]');
      if (!button) return;
      event.preventDefault(); event.stopImmediatePropagation();
      if (cartBusy) return;
      button.disabled = true;
      try {
        await request(`${root}/cart/update.js`, { method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify({note:drawer.querySelector('[name="note"]').value}) });
        const status = drawer.querySelector('[data-cto-note-status]');
        if (status) status.textContent = context.strings.saved;
        announce(context.strings.saved);
      } catch (error) { showError(error); }
      finally { button.disabled = false; }
    }, true);
  }
  document.addEventListener('keydown', event => {
    if (isPage || drawer.getAttribute('aria-hidden')!=='false' || document.querySelector('.cto-mini-cart-discount-modal:not([hidden])')) return;
    if (event.key==='Escape') { event.preventDefault(); close(); }
    if (event.key==='Tab') {
      const focusable = Array.from(drawer.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), summary')).filter(el=>el.getClientRects().length && !el.closest('[hidden]'));
      const first=focusable[0],last=focusable.at(-1);
      if (!drawer.contains(document.activeElement)) {event.preventDefault();(event.shiftKey ? last : first)?.focus();}
      else if (event.shiftKey && (document.activeElement===first || document.activeElement===drawer)) {event.preventDefault();last?.focus();}
      else if(!event.shiftKey && document.activeElement===last){event.preventDefault();first?.focus();}
    }
  });
  context = parseContext();
  if (isPage) renderExtras();
  };
  document.querySelectorAll('#mini-cart.cto-order-drawer, [data-cto-order-page]').forEach(initialize);
  const packMessages = JSON.parse(document.getElementById('cto-pack-messages')?.textContent || '{}');
  const approvedForms = new WeakSet();
  let checkingPacks = false;
  const packNotice = message => {
    const visible = surfaces.filter(surface => surface.element.hasAttribute('data-cto-order-page') || surface.element.getAttribute('aria-hidden') === 'false');
    if (visible.length) visible.forEach(surface => surface.showError(new Error(message)));
    else window.alert(message);
  };
  const packRequest = async (url, options = {}) => {
    const controller = new AbortController(), timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      if (!response.ok) throw new Error('unavailable');
      return await response.json();
    } finally { clearTimeout(timeout); }
  };
  const syncCartInputs = data => {
    const quantities = new Map(data.items.map(item => [item.key, item.quantity]));
    document.querySelectorAll('input.quantity-selector__value[data-line-key]').forEach(input => {
      if (quantities.has(input.dataset.lineKey)) input.value = String(quantities.get(input.dataset.lineKey));
    });
  };
  const verifyCheckout = async continueCheckout => {
    if (checkingPacks) return;
    if (cartBusy) { packNotice(packMessages.updating); return; }
    checkingPacks = true;
    cartBusy = true;
    surfaces.forEach(surface => surface.setBusy(true));
    let proceed = false;
    const root = (window.Shopify?.routes?.root || window.routes?.rootUrl || '/').replace(/\/$/, '');
    try {
      const data = await packRequest(`${root}/cart?view=cto-pack-rules&_=${Date.now()}`);
      const updates = planCartUpdates(data);
      if (!data.items.length) throw new Error('empty');
      if (Object.keys(updates).length) {
        await packRequest(`${root}/cart/update.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ updates }) });
        const verified = await packRequest(`${root}/cart?view=cto-pack-rules&_=${Date.now()}`);
        if (Object.keys(planCartUpdates(verified)).length) throw new Error('invalid');
        await Promise.all(surfaces.map(surface => surface.refresh()));
        packNotice(packMessages.adjusted);
      } else {
        syncCartInputs(data);
        proceed = true;
      }
    } catch (error) {
      packNotice(error.message === 'empty' ? packMessages.empty : error.message === 'invalid' ? packMessages.invalid : packMessages.checkout_unavailable);
    } finally {
      checkingPacks = false;
      cartBusy = false;
      surfaces.forEach(surface => surface.setBusy(false));
      if (proceed) continueCheckout();
    }
  };
  const normalizeProductForm = (form, event) => {
    if (!form?.matches('form[action*="/cart/add"]')) return;
    const input = form.querySelector('[name="quantity"][data-cto-pack]');
    if (!input) return;
    const quantity = normalize(input.value, input.dataset.ctoPack, input.min, input.max, input.dataset.ruleIncrement);
    if (quantity === null) {
      event.preventDefault(); event.stopImmediatePropagation(); packNotice(packMessages.invalid);
    } else if (Number(input.value) !== quantity) {
      input.value = String(quantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };
  document.addEventListener('click', event => {
    const button = event.target.closest('button, input[type="submit"]');
    if (button?.type === 'submit') normalizeProductForm(button.form, event);
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/i.test(url.pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    verifyCheckout(() => location.assign(url.href));
  }, true);
  document.addEventListener('submit', event => {
    const form = event.target;
    normalizeProductForm(form, event);
    if (approvedForms.has(form)) return;
    if (!form.matches('form[action]')) return;
    const path = new URL(form.action, location.origin).pathname;
    const submitter = event.submitter;
    const checkout = /\/checkout\/?$/.test(path) || (/\/cart\/?$/.test(path) && (submitter?.name === 'checkout' || form.querySelector('[name="checkout"]')));
    if (!checkout) return;
    event.preventDefault(); event.stopImmediatePropagation();
    verifyCheckout(() => {
      approvedForms.add(form);
      const button = submitter?.isConnected ? submitter : form.querySelector('[name="checkout"]');
      try { form.requestSubmit(button || undefined); } finally { approvedForms.delete(form); }
    });
  }, true);
})();

